import axios from 'axios';
import { getToken } from './auth';
import { invalidateProducts } from '../utils/productEventsHub';

const API_URL = 'https://backend.chamafruits.com';

const withAuth = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
  withCredentials: true,
});

// 📦 Products
export const getProducts = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/`, { ...withAuth(), params })).data;

export const downloadPriceListPdf = async (branchId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf`, {
    ...withAuth(),
    params,
    responseType: 'blob',
  })).data;

export const requestPriceListPdfJob = async (branchId, payload = {}) =>
  (await axios.post(`${API_URL}/branch/${branchId}/product/price-list/pdf/jobs`, payload, withAuth())).data;

export const getPriceListPdfJobStatus = async (branchId, jobId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf/jobs/${jobId}`, withAuth())).data;

export const downloadPriceListPdfJob = async (branchId, jobId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/product/price-list/pdf/download/${jobId}`, {
    ...withAuth(),
    responseType: 'blob',
  })).data;

export const createProduct = async (branchId, product) => {
  product.productName = product.productName.trim().toLowerCase();
  product.productName = product.productName.charAt(0).toUpperCase() + product.productName.slice(1);
  const data = (await axios.post(`${API_URL}/branch/${branchId}/product`, product, withAuth())).data;
  invalidateProducts(branchId, { action: 'create-product', productId: data?.id, product: data });
  return data;
};

export const updateProduct = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-product',
    productId: id,
    product: response || { id, ...data },
  });
  return response;
};

export const deleteProduct = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-product', productId: id });
  return response;
};

// 🌱 Varieties
export const addVarietyToProduct = async (branchId, data) => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/variety`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'add-variety',
    varietyId: response?.id,
    productId: response?.productId ?? data?.productId,
    variety: response,
  });
  return response;
};

export const updateVariety = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-variety',
    varietyId: id,
    variety: response || { id, ...data },
  });
  return response;
};

export const deleteVariety = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/variety/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-variety', varietyId: id });
  return response;
};

export const markAsSold = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}/sold-out`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'variety-sold-out',
    varietyId: id,
    variety: response || { id, isSoldOut: true },
    isSoldOut: true,
  });
  return response;
};

export const markAsAvailable = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety/${id}/available`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'variety-available',
    varietyId: id,
    variety: response || { id, isSoldOut: false },
    isSoldOut: false,
  });
  return response;
};

export const markPriceAsSold = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}/sold-out`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'price-sold-out',
    priceId: id,
    price: response || { id, isSoldOut: true },
    isSoldOut: true,
  });
  return response;
};

export const markPriceAsAvailable = async (branchId, id) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}/available`, {}, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'price-available',
    priceId: id,
    price: response || { id, isSoldOut: false },
    isSoldOut: false,
  });
  return response;
};

// 💰 Variety Prices
export const addVarietyPriceToVariety = async (branchId, data) => {
  const response = (await axios.post(`${API_URL}/branch/${branchId}/product/variety-price`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'add-variety-price',
    priceId: response?.id,
    varietyId: response?.varietyId ?? data?.varietyId,
    price: response,
  });
  return response;
};

export const updateVarietyPrice = async (branchId, id, data) => {
  const response = (await axios.patch(`${API_URL}/branch/${branchId}/product/variety-price/${id}`, data, withAuth())).data;
  invalidateProducts(branchId, {
    action: 'update-variety-price',
    priceId: id,
    price: response || { id, ...data },
  });
  return response;
};

export const removeVarietyPrice = async (branchId, id) => {
  const response = (await axios.delete(`${API_URL}/branch/${branchId}/product/variety-price/${id}`, withAuth())).data;
  invalidateProducts(branchId, { action: 'delete-variety-price', priceId: id });
  return response;
};

export const getPriceByBarcode = async (branchId, barcode, clientType) => {
  const params = new URLSearchParams();
  if (barcode) params.set('barcode', barcode);
  if (clientType) params.set('clientType', clientType);
  const query = params.toString();
  const suffix = query ? `?${query}` : '';
  return (await axios.get(`${API_URL}/branch/${branchId}/product/prices/by-barcode${suffix}`)).data;
};
